import {
    Button,
    CircularProgress,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import React, { useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { useNotify } from '../../../hooks/useNotify';
import { doSwaggerCall, useSwaggerClient } from '../../../hooks/useSwaggerClient';
import ManifestGuestsTable from './components/ManifestGuestsTable';
import moment from 'moment';
import { ExpandMoreIcon } from '../../../icons';
import { buildManifestReport, downloadManifestWorkbook } from '../../../manifestReport';

const ManifestGuestsPage = () => {
    const location = useLocation();
    const { id, timeslot } = useParams();
    const [sendManifest, setSendManifest] = useState(false);
    const history = useHistory();
    const { notifySuccess, notifyError } = useNotify();

    // Get activity_start from URL path param, or fallback to state/query for legacy routes
    const searchParams = new URLSearchParams(location.search);
    const activityStart =
        (timeslot && decodeURIComponent(timeslot)) ||
        location.state?.activity_start ||
        searchParams.get('start');

    const formattedActivityStart = activityStart
        ? moment(activityStart).format('ddd MMM DD, YYYY [at] hh:mm A')
        : null;

    const [guestsData, guestsLoading, guestsError] = useSwaggerClient(
        'activities',
        'collectGuestDatasForManifest',
        {
            activityId: id,
            activity_start: activityStart
        }
    );

    // Sort guests by last name (A-Z) then first name, tickets by name alphabetically
    const sortedGuests = useMemo(() => {
        if (!guestsData?.guests) {
            return [];
        }
        return [...guestsData.guests]
            .sort((a, b) => (a.guestName || '').localeCompare(b.guestName || ''))
            .map(guest => ({
                ...guest,
                tickets: [...guest.tickets].sort((a, b) =>
                    (a.name || '').localeCompare(b.name || '')
                )
            }));
    }, [guestsData?.guests]);

    const sendManifestEmail = async () => {
        const vendorEmail = guestsData?.generalDatas?.vendor.email;
        await doSwaggerCall('activities', 'collectGuestDatasForManifest', {
            activityId: id,
            activity_start: activityStart,
            vendorEmail
        })
            .then(() => {
                notifySuccess(`Manifest sent to the vendor`);
            })
            .catch(() => {
                notifyError(`Manifest cannot be sent to the vendor`);
            });
    };

    // The workbook is built here from the guest list — the backend used to ship
    // a prebuilt file, but the report is now shaped on the frontend
    // (client/src/manifestReport.js), same as in fullwidget.
    const downloadManifest = () => {
        const activity = guestsData?.generalDatas?.activity;
        const guests = guestsData?.guests || [];
        if (!guests.length) {
            return;
        }
        const report = buildManifestReport(activity?.start || activityStart, activity?.end, guests);
        downloadManifestWorkbook(
            report,
            `${activity?.name || 'manifest'}-${formattedActivityStart || 'date'}`
        );
    };

    const hasGuests = sortedGuests.length > 0;

    const tickets = useMemo(() => {
        const ticketTypesMap = new Map();
        sortedGuests.forEach(guest => {
            guest.tickets.forEach(ticket => {
                const prev = ticketTypesMap.get(ticket.name) || 0;
                ticketTypesMap.set(ticket.name, prev + ticket.quantity);
            });
        });
        return Array.from(ticketTypesMap.entries()).map(([name, quantity]) => ({
            name,
            quantity
        }));
    }, [sortedGuests]);

    return (
        <Grid container className="bg-white client-table page">
            <Grid item xs={12} container justifyContent="space-between">
                <Grid item xs={6}>
                    <h3>{guestsData?.generalDatas?.activity.name}</h3>
                    <h4>{formattedActivityStart}</h4>
                </Grid>
                <Grid item xs={6} style={{ textAlign: 'end' }}>
                    <Button
                        onClick={() => history.goBack()}
                        className="link mb-2"
                        style={{ color: window.clientData?.primaryColor }}
                    >
                        Back
                    </Button>
                </Grid>
                <Grid item xs={12}>
                    {hasGuests && !guestsError && <h5>Ticket total: {guestsData?.ticketNumber}</h5>}
                </Grid>
                <Grid item xs={12} md={6} style={{ padding: '15px' }}>
                    {hasGuests ? (
                        <h5>
                            Manifest to:{' '}
                            {Array.isArray(guestsData?.manifestEmail)
                                ? guestsData.manifestEmail.join(', ')
                                : guestsData?.manifestEmail}
                        </h5>
                    ) : (
                        <h5>There are 0 guests in this timeslot</h5>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={async () => {
                            setSendManifest(true);
                            try {
                                await sendManifestEmail();
                            } catch (err) {
                                console.log(err);
                            }
                            setSendManifest(false);
                        }}
                        disabled={!hasGuests || guestsError}
                    >
                        {sendManifest ? (
                            <CircularProgress color="success" size={25} />
                        ) : (
                            'Send manifest'
                        )}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        style={{ marginLeft: '10px' }}
                        onClick={downloadManifest}
                        disabled={!hasGuests || guestsError}
                    >
                        Export manifest
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        style={{ marginLeft: '10px' }}
                        component={Link}
                        to={`/client/bookings?activity=${id}&startdatetime=${encodeURIComponent(
                            activityStart || ''
                        )}`}
                    >
                        View bookings
                    </Button>
                </Grid>
                <Grid
                    item
                    xs={12}
                    md={6}
                    style={{
                        padding: '15px',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'flex-start'
                    }}
                >
                    <div style={{ width: '100%', maxWidth: '360px' }}>
                        <Accordion elevation={1}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <div
                                    style={{
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        paddingRight: '8px'
                                    }}
                                >
                                    <span style={{ fontWeight: 600 }}>Ticket summary</span>
                                    <span style={{ color: '#666', fontSize: '14px' }}>
                                        {[...tickets].reduce(
                                            (sum, ticket) => sum + ticket.quantity,
                                            0
                                        )}{' '}
                                        tickets
                                    </span>
                                </div>
                            </AccordionSummary>

                            <AccordionDetails style={{ display: 'block', paddingTop: 0 }}>
                                {[...tickets]
                                    .sort((a, b) => b.quantity - a.quantity)
                                    .map(ticket => (
                                        <div
                                            key={ticket.name}
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '8px 0',
                                                borderBottom: '1px solid #ececec'
                                            }}
                                        >
                                            <span style={{ color: '#444' }}>{ticket.name}</span>
                                            <span style={{ fontWeight: 600 }}>
                                                {ticket.quantity}
                                            </span>
                                        </div>
                                    ))}
                            </AccordionDetails>
                        </Accordion>
                    </div>
                </Grid>
            </Grid>
            {guestsError ? (
                <Grid item xs={12}>
                    <Alert severity="error">
                        <h4>{guestsError.response.body.message || guestsError.message}</h4>
                    </Alert>
                </Grid>
            ) : guestsLoading ? (
                <CircularProgress />
            ) : (
                <ManifestGuestsTable guests={sortedGuests} />
            )}
        </Grid>
    );
};

export default ManifestGuestsPage;
